import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { montarResumoCurto, interpretarTotal, rotuloReferencia, formatarDataHora } from '../../lib/report';
import { MEEM_TOTAL_MAX } from '../../lib/meemItems';
import { RULER_GROUPS, batchShare, groupPartial, referenceMark } from '../../lib/guidedFlow';
import { buildLocalSnapshot } from '../../lib/resultSnapshot';
import { createAnonymousHandoff } from '../../lib/resultHandoff';
import PrintSheet from './PrintSheet';
import DoutoResultCta from './DoutoResultCta';
import DoutoBanner from '../DoutoBanner';
import GuiaBanner from '../GuiaBanner';
import { accountCtaEnabled, doutoBannersEnabled } from '../../lib/flags';
import EducationChips from './EducationChips';
import SaveToPatient from './SaveToPatient';
import EvolucaoCard from './EvolucaoCard';
import { ResultRuler } from './ScoreRuler';
import SafeIcon from '../../common/SafeIcon';

const ESCOLARIDADE = {
  noFormalEducation: 'sem escolaridade formal', years1To4: '1–4 anos', years5To8: '5–8 anos',
  years9To11: '9–11 anos', years12OrMore: '12+ anos', none: 'não informada'
};
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
const reducedMotion = () => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

/** Coluna rolável: marca se há mais conteúdo acima/abaixo (para a sombra sutil) sem criar rolagem aninhada. */
function ScrollCol({ className, children, ...rest }) {
  const ref = useRef(null);
  const [more, setMore] = useState({ top: false, bottom: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setMore((m) => {
      const next = { top: el.scrollTop > 2, bottom: el.scrollHeight - el.clientHeight - el.scrollTop > 2 && el.scrollHeight > el.clientHeight };
      return m.top === next.top && m.bottom === next.bottom ? m : next;
    });
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    Array.from(el.children).forEach((c) => ro?.observe(c));
    return () => { el.removeEventListener('scroll', update); ro?.disconnect(); };
  }, []);
  return <div ref={ref} className={`scroll-col ${className}`} data-more-top={more.top} data-more-bottom={more.bottom} {...rest}>{children}</div>;
}

/** Botão só com ícone no rodapé (Copiar/Imprimir/Editar): o rótulo continua sendo o nome acessível via <span className="sr-only">, só não fica visível na tela. */
function IconButton({ icon, label, ...rest }) {
  return (
    <button type="button" className="btn-secondary !h-11 !w-11 !p-0" {...rest}>
      <SafeIcon name={icon} className="h-[18px] w-[18px]" />
      <span className="sr-only">{label}</span>
    </button>
  );
}

/**
 * Modal de resultado (dialog acessível), enxuto de propósito: responde só 4 perguntas —
 * qual foi a pontuação, a avaliação foi concluída, como pontuou por domínio e o que fazer
 * com o resultado (link "Ver interpretação completa" para /avaliacao/:token). O restante
 * (detalhamento por item, domínios em tabela, leitura normativa, texto de prontuário
 * editável) mora na página de interpretação. Rodapé fixo: copiar, imprimir, editar
 * respostas e guardar no histórico. No celular vira bottom sheet quase em tela cheia.
 *
 * Foco preso no diálogo, Esc e "Fechar", foco devolvido a quem abriu (ou ao botão do fim da calculadora),
 * rolagem do fundo travada sem mover a página, fundo inerte. Nada aqui vai para URL ou analytics,
 * exceto o token opaco (UUID) da página de interpretação, gerado por resultHandoff.js.
 */
export default function ResultModal({
  open, onClose, fallbackFocusId = 'ver-resultado-fim',
  total, education, educationAnswered, reference, ageConfirm, checked = {}, batch = {}, blockScores, mode, unevaluated = 0,
  onEducation, onEdit, onGoFirst
}) {
  const panelRef = useRef(null);
  const titleRef = useRef(null);
  const openerRef = useRef(null);
  const restoreRef = useRef(true);
  const [copiado, setCopiado] = useState(false);
  const interpRef = useRef(null);
  const prevEducation = useRef(educationAnswered ? education : null);
  const [announce, setAnnounce] = useState('');
  const [aplicadoEm, setAplicadoEm] = useState(() => formatarDataHora());
  const [saveSignal, setSaveSignal] = useState(0);
  const [handoffToken, setHandoffToken] = useState(null);
  const [session, setSession] = useState('unknown'); // unknown | in | out (só para escolher o texto do cartão de conta)
  const accountCta = accountCtaEnabled();
  const banners = doutoBannersEnabled();
  const [copiou, setCopiou] = useState(false); // depois de copiar, a mensagem do Douto ocupa o lugar dos outros banners do modal
  const [lateralPronta, setLateralPronta] = useState(false); // o banner lateral é `fixed`: só entra depois da animação de entrada (que usa transform)
  const current = educationAnswered ? education : null;

  // Token da página de interpretação completa: atualiza sempre que o resultado muda,
  // para o link nunca apontar para um escore ou escolaridade já desatualizados. Mora
  // aqui (não em resultHandoff.js em si) só o efeito de recalcular; a escrita real no
  // sessionStorage é feita por createAnonymousHandoff, de propósito fora deste arquivo.
  useEffect(() => {
    if (!open) { setHandoffToken(null); return; }
    setHandoffToken(createAnonymousHandoff(buildLocalSnapshot({ total, education, educationAnswered, reference, ageConfirm, checked, batch, mode, unevaluated })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, total, education, educationAnswered, reference, ageConfirm, mode, unevaluated, checked, batch]);

  // Revelação: anuncia por aria-live e leva o foco ao título da interpretação na primeira revelação.
  useEffect(() => {
    if (!open) { prevEducation.current = current; return; }
    const before = prevEducation.current;
    prevEducation.current = current;
    if (before === current) return;
    if (current === null) { setAnnounce('Interpretação ocultada: escolaridade não selecionada.'); return; }
    if (before === null) {
      setAnnounce('Interpretação disponível.');
      interpRef.current?.focus({ preventScroll: false });
    } else {
      setAnnounce(`Interpretação atualizada para escolaridade ${ESCOLARIDADE[current]}.`);
    }
  }, [current, open]);

  useEffect(() => {
    if (!open || current === null || session !== 'unknown') return undefined;
    let alive = true;
    fetch('/api/auth/session', { credentials: 'same-origin' })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => { if (alive) setSession(data?.user ? 'in' : 'out'); })
      .catch(() => { if (alive) setSession('out'); });
    return () => { alive = false; };
  }, [open, current, session]);

  const close = (reason = 'dismiss') => { restoreRef.current = reason === 'dismiss'; onClose(reason); };

  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    restoreRef.current = true;
    setAplicadoEm(formatarDataHora());
    setCopiou(false);
    setLateralPronta(false);
    const lateralTimer = setTimeout(() => setLateralPronta(true), reducedMotion() ? 0 : 300);
    const root = document.getElementById('root');
    const html = document.documentElement;
    const previous = { overflow: html.style.overflow, paddingRight: html.style.paddingRight };
    const scrollbar = window.innerWidth - html.clientWidth;
    html.style.overflow = 'hidden'; // trava o fundo sem mexer na posição de rolagem
    if (scrollbar > 0) html.style.paddingRight = `${scrollbar}px`;
    html.dataset.resultOpen = '1';
    root?.setAttribute('inert', '');
    root?.setAttribute('aria-hidden', 'true');
    titleRef.current?.focus({ preventScroll: true });

    const onKeyDown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); close('dismiss'); return; }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter((el) => { const d = el.closest('details:not([open])'); return !d || el.parentElement === d; });
      if (!items.length) { event.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === titleRef.current || !panelRef.current.contains(active))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (active === last || !panelRef.current.contains(active))) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      clearTimeout(lateralTimer);
      document.removeEventListener('keydown', onKeyDown, true);
      html.style.overflow = previous.overflow;
      html.style.paddingRight = previous.paddingRight;
      delete html.dataset.resultOpen;
      root?.removeAttribute('inert');
      root?.removeAttribute('aria-hidden');
      if (restoreRef.current) {
        const opener = openerRef.current;
        const target = opener && opener !== document.body && document.contains(opener) ? opener : document.getElementById(fallbackFocusId);
        target?.focus({ preventScroll: true });
      }
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || typeof document === 'undefined') return null;

  const { texto: interpretacao } = interpretarTotal({ total, education, reference, ageConfirm });
  const domains = mode === 'domains';
  const mark = domains ? referenceMark({ education, reference, ageConfirm }) : null;
  const partials = RULER_GROUPS.map((group) => ({ group, ...groupPartial(checked, group) }));
  const erros = domains ? Object.values(checked).filter((v) => v === false).length : 0;
  const acertos = domains ? Object.values(checked).filter((v) => v === true).length : total;

  const resumoAuto = montarResumoCurto({
    total,
    grupos: domains ? partials.map(({ group, hits, max }) => ({ copy: group.copy, hits, max })) : [],
    education,
    referencia: mark,
    naoAvaliados: unevaluated,
    emBloco: domains && batchShare(batch) > 0.8,
    aplicadoEm
  });
  // Copiar funciona direto, sem exigir que o texto fique visível antes: quem quiser
  // revisar ou editar o texto antes de copiar faz isso na página de interpretação completa.
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(resumoAuto);
    } catch {
      // Sem permissão de área de transferência, o texto ainda precisa chegar ao profissional de algum jeito.
      window.prompt('Copie o resumo abaixo:', resumoAuto);
    }
    window.eidocAnalytics?.capture('assessment_result_copied', {});
    setCopiado(true);
    setCopiou(true);
    setTimeout(() => setCopiado(false), 2500);
  };

  const animate = !reducedMotion();
  // Com a flag desligada o atalho de ficha aparece sempre; com ela ligada, só depois da escolaridade (ou "Não informar").
  const showSave = accountCta ? current !== null : true;
  const linkBtn = 'min-h-11 text-[length:var(--fs-15)] font-medium text-[var(--accent)] underline';

  return (<>{createPortal(
    <div className="result-modal fixed inset-0 z-[60] flex items-end justify-center md:items-center md:p-4" data-result-modal>
      <div className={`absolute inset-0 bg-[rgb(27_30_36/0.55)] ${animate ? 'backdrop-in' : ''}`} onClick={() => close('dismiss')} aria-hidden="true" data-backdrop />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="resultado-titulo"
        style={{ opacity: 1 }}
        className={`relative flex h-[calc(100dvh-1.5rem)] w-full min-w-0 flex-col overflow-hidden rounded-t-[12px] bg-[var(--surface)] md:h-auto md:max-h-[min(88vh,760px)] md:max-w-[640px] md:rounded-[12px] ${animate ? 'sheet-in' : ''}`}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--line)] px-4 py-1 md:px-6">
          <h2 id="resultado-titulo" ref={titleRef} tabIndex={-1} className="text-[length:var(--fs-17)] font-semibold outline-none">Resultado</h2>
          <button type="button" onClick={() => close('dismiss')} className="inline-flex min-h-11 items-center gap-1.5 rounded-[6px] px-2 text-[length:var(--fs-15)] font-medium text-[var(--ink)] hover:bg-[var(--hover)]">
            <SafeIcon name="X" className="h-4 w-4" />Fechar
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden" data-result-body>
          <ScrollCol className="px-4 py-4 md:px-6 md:pb-6">
          {/* (a) escore e régua: não dependem de escolaridade */}
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <p className="font-display text-[length:var(--fs-64)] font-semibold leading-none tracking-[-0.02em] text-[var(--ink)]" style={{ fontVariantNumeric: 'tabular-nums' }} aria-label={`${total} de ${MEEM_TOTAL_MAX}`}>
              {total} de {MEEM_TOTAL_MAX}
            </p>
            {domains && <p className="text-[length:var(--fs-14)] text-[var(--ink-2)]">{acertos} {acertos === 1 ? 'acerto' : 'acertos'} · {erros} {erros === 1 ? 'erro' : 'erros'} · {unevaluated} não avaliados</p>}
          </div>
          {domains && <div className="mt-3"><ResultRuler checked={checked} cut={mark} /></div>}

          {domains && unevaluated > 0 && (
            <p role="status" className="mt-2 text-[length:var(--fs-14)] text-[var(--ink)]">
              {unevaluated} {unevaluated === 1 ? 'item não avaliado' : 'itens não avaliados'} — o total pode estar subestimado.{' '}
              <button type="button" onClick={() => { close('first'); onGoFirst(); }} className="min-h-11 font-semibold text-[var(--accent)] underline">Ir ao primeiro</button>
            </p>
          )}

          {/* (b) escolaridade: pergunta depois do resultado; "Não informar" libera tudo e nada trava */}
          <section aria-labelledby="escolaridade-titulo" className="mt-3 rounded-[8px] border border-[var(--cell-border)] bg-[var(--surface)] p-3" data-schooling>
            <h3 id="escolaridade-titulo" className="sr-only">Escolaridade</h3>
            <EducationChips
              value={educationAnswered ? education : null}
              onChange={onEducation}
              name="escolaridade-resultado"
              legend="Qual a escolaridade do paciente?"
              hint="só muda a leitura do resultado"
            />
          </section>

          {/* aria-live: a revelação e a troca de escolaridade são anunciadas sem mover o foco a cada troca */}
          <p role="status" aria-live="polite" className="sr-only" data-reveal-status>{announce}</p>

          {/* (c) interpretação, só depois da escolha: texto breve + link para a página completa */}
          {educationAnswered && (
            <section aria-labelledby="interpretacao-titulo" className={`mt-4 ${animate ? 'reveal-in' : ''}`} data-interpretation>
              <h3 id="interpretacao-titulo" ref={interpRef} tabIndex={-1} className="scroll-mt-4 font-sans text-[length:var(--fs-16)] font-semibold outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]">Interpretação</h3>
              <p className="mt-1 max-w-[62ch] text-[length:var(--fs-16)] leading-relaxed text-[var(--ink)]">{interpretacao}</p>
              <p className="mt-2 text-[length:var(--fs-14)] text-[var(--ink-2)]">
                Escolaridade informada: <strong className="font-semibold text-[var(--ink)]">{ESCOLARIDADE[education]}</strong>. Referência: <strong className="font-semibold text-[var(--ink)]">{rotuloReferencia(reference)}</strong>.
              </p>
              <p className="mt-2">
                <Link
                  to={handoffToken ? `/avaliacao/${handoffToken}` : '#'}
                  onClick={() => window.eidocAnalytics?.capture('assessment_interpretation_opened', { source: 'local' })}
                  className="inline-flex min-h-11 items-center font-semibold text-[var(--accent)] underline"
                >
                  Ver interpretação completa →
                </Link>
              </p>
            </section>
          )}

          {/* próximo passo → Douto, logo depois da interpretação (com os banners ligados, eles assumem esse papel) */}
          {!banners && <DoutoResultCta />}
          {banners && !copiou && <DoutoBanner placement="resultado-rodape" variant="linha" className="mt-3 min-[1560px]:hidden" />}

          {!copiou && <GuiaBanner placement="resultado-rodape" variant="linha" className="mt-3" />}

          {/* promessa de produto: evolução do paciente (prévia real com o ponto de hoje) */}
          {educationAnswered && session !== 'in' && <EvolucaoCard total={total} onStart={() => setSaveSignal((n) => n + 1)} />}

          {/* salvar na ficha (abre aqui quando o atalho do rodapé é acionado) */}
          <div className="mt-3">
            <SaveToPatient variant="hosted" startSignal={saveSignal} total={total} education={education} reference={reference} ageConfirm={ageConfirm} blockScores={blockScores} mode={mode} />
          </div>

          </ScrollCol>
        </div>

        <footer className="shrink-0 border-t border-[var(--line)] bg-[var(--surface)] px-4 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:px-6" data-result-footer>
          {/* Copiar/Imprimir/Editar são ações secundárias e frequentes: viram ícones para caber numa
              linha só. Guardar é o único botão grande — é o passo que leva a criar conta. */}
          <div className="flex flex-wrap items-center gap-2">
            <IconButton icon="Copy" label="Copiar para o prontuário" onClick={copiar} />
            <IconButton icon="Printer" label="Imprimir folha de registro" onClick={() => window.print()} />
            <IconButton icon="Pencil" label="Editar respostas" onClick={() => { close('edit'); onEdit(); }} />
            {showSave && (
              <>
                <button
                  type="button"
                  data-analytics-event={accountCta ? 'calculator_account_cta_clicked' : 'calculator_save_clicked'}
                  onClick={() => setSaveSignal((n) => n + 1)}
                  className="btn-secondary min-w-0 flex-1 gap-2 !px-3.5 !py-2"
                >
                  <SafeIcon name="Bookmark" className="h-4 w-4 shrink-0" />
                  Guardar e acompanhar a evolução
                </button>
                {accountCta && session !== 'in' && <button type="button" onClick={() => setSaveSignal((n) => n + 1)} className={`${linkBtn} shrink-0 !text-[var(--ink-2)]`}>Já tenho conta</button>}
              </>
            )}
            <div className="ml-auto flex shrink-0 items-center">
              {banners && copiou ? (
                <>
                  <p role="status" aria-live="polite" className="sr-only">Copiado</p>
                  <DoutoBanner placement="copiou-prontuario" variant="mensagem" />
                </>
              ) : (
                <p role="status" aria-live="polite" className="text-[length:var(--fs-14)] text-[var(--ink-2)]">{copiado ? 'Copiado' : ''}</p>
              )}
            </div>
          </div>
        </footer>
        {banners && lateralPronta && !copiou && (
          <div className="fixed left-[calc(50%+556px)] top-1/2 hidden w-[208px] -translate-y-1/2 min-[1560px]:block" data-lateral-slot>
            <DoutoBanner placement="resultado-lateral" variant="lateral" />
          </div>
        )}
      </div>
    </div>,
    document.body
  )}{createPortal(
    <PrintSheet
      total={total}
      education={education}
      reference={reference}
      ageConfirm={ageConfirm}
      checked={checked}
      batch={batch}
      blockScores={blockScores}
      mode={mode}
      unevaluated={unevaluated}
    />,
    document.body
  )}</>);
}
